import { useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import { notificationKeys } from '@/features/notifications/hooks';
import { projectKeys, taskKeys, useCreateTask } from '@/features/projects/hooks';
import { workRpc } from '@/features/workMode/api';
import type {
  Inserts,
  Tables,
  TicketAttachmentBucket,
  TicketPriority,
  TicketStatus,
  Updates,
} from '@/types/database';
import { isTicketClosed } from './model';

/**
 * Hooks for Tickets (migration 143).
 *
 * The browser talks to the ticket tables directly; RLS decides what each
 * person sees (triage tier: everything in the org; everyone else: what
 * they requested, handle, created or follow) and the BEFORE UPDATE
 * trigger decides which fields they may change. Activity events and
 * notifications are written by triggers, so the client never inserts
 * into ticket_events or notifications.
 *
 * Work Mode tablets are signed in as a kiosk account and go through the
 * `work_ticket_*` RPCs instead (bottom of this file): the worker is
 * identified by their open session, never by auth.uid().
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Ticket = Tables<'tickets'>;
export type TicketCategory = Tables<'ticket_categories'>;
export type TicketComment = Tables<'ticket_comments'>;
export type TicketAttachment = Tables<'ticket_attachments'>;
export type TicketEvent = Tables<'ticket_events'>;
export type TicketWatcher = Tables<'ticket_watchers'>;

export interface TicketPerson {
  id: string;
  full_name: string | null;
  email: string;
  avatar_url: string | null;
}

export interface TicketWithContext extends Ticket {
  requester: TicketPerson | null;
  assignee: TicketPerson | null;
  creator: TicketPerson | null;
  category: Pick<TicketCategory, 'id' | 'name' | 'slug'> | null;
  workstation: { id: string; name: string } | null;
  item: { id: string; name: string; sku: string | null } | null;
  linked_task: {
    id: string;
    name: string;
    project_id: string;
    is_completed: boolean;
  } | null;
}

export type TicketCommentWithAuthor = TicketComment & { author: TicketPerson | null };
export type TicketEventWithActor = TicketEvent & { actor: TicketPerson | null };
export type TicketAttachmentWithUploader = TicketAttachment & { uploader: TicketPerson | null };
export type TicketWatcherWithProfile = TicketWatcher & { profile: TicketPerson | null };

const PERSON = 'id, full_name, email, avatar_url';

const TICKET_SELECT =
  '*, ' +
  `requester:requester_id(${PERSON}), ` +
  `assignee:assignee_id(${PERSON}), ` +
  `creator:created_by(${PERSON}), ` +
  'category:category_id(id, name, slug), ' +
  'workstation:workstation_id(id, name), ' +
  'item:item_id(id, name, sku), ' +
  'linked_task:linked_task_id(id, name, project_id, is_completed)';

export const ticketKeys = {
  all: ['tickets'] as const,
  list: () => [...ticketKeys.all, 'list'] as const,
  detail: (id: string) => [...ticketKeys.all, 'detail', id] as const,
  comments: (id: string) => [...ticketKeys.all, 'comments', id] as const,
  events: (id: string) => [...ticketKeys.all, 'events', id] as const,
  attachments: (id: string) => [...ticketKeys.all, 'attachments', id] as const,
  watchers: (id: string) => [...ticketKeys.all, 'watchers', id] as const,
  categories: () => [...ticketKeys.all, 'categories'] as const,
  signedUrl: (bucket: string, path: string) =>
    [...ticketKeys.all, 'signed-url', bucket, path] as const,
  work: {
    list: (sessionId: string) => [...ticketKeys.all, 'work', 'list', sessionId] as const,
    thread: (sessionId: string, ticketId: string) =>
      [...ticketKeys.all, 'work', 'thread', sessionId, ticketId] as const,
  },
};

// Gentle poll as a fallback for when the realtime channel drops.
const POLL_MS = 60_000;

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

export function useTicketCategories(options?: { includeInactive?: boolean }) {
  const { profile } = useAuth();
  const includeInactive = options?.includeInactive ?? false;
  return useQuery({
    queryKey: [...ticketKeys.categories(), includeInactive ? 'all' : 'active'],
    enabled: !!profile,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      let q = supabase
        .from('ticket_categories')
        .select('*')
        .order('position', { ascending: true })
        .order('name', { ascending: true });
      if (!includeInactive) q = q.eq('is_active', true);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as TicketCategory[];
    },
  });
}

export interface SaveTicketCategoryInput {
  id?: string;
  name: string;
  slug?: string;
  description?: string | null;
  defaultAssigneeId?: string | null;
  isActive?: boolean;
  position?: number;
}

function slugify(name: string): string {
  const s = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  return s || 'category';
}

export function useSaveTicketCategory() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: SaveTicketCategoryInput) => {
      if (!profile) throw new Error('Not signed in.');
      const name = input.name.trim();
      if (!name) throw new Error('Give the category a name.');
      if (input.id) {
        const patch: Updates<'ticket_categories'> = {
          name,
          description: input.description?.trim() || null,
          default_assignee_id: input.defaultAssigneeId ?? null,
          ...(input.isActive === undefined ? {} : { is_active: input.isActive }),
          ...(input.position === undefined ? {} : { position: input.position }),
        };
        const { data, error } = await supabase
          .from('ticket_categories')
          .update(patch)
          .eq('id', input.id)
          .select('*')
          .single();
        if (error) throw error;
        return data as TicketCategory;
      }
      const payload: Inserts<'ticket_categories'> = {
        organization_id: profile.organization_id,
        slug: input.slug?.trim() || `${slugify(name)}_${Date.now().toString(36).slice(-4)}`,
        name,
        description: input.description?.trim() || null,
        default_assignee_id: input.defaultAssigneeId ?? null,
        is_active: input.isActive ?? true,
        created_by: profile.id,
      };
      const { data, error } = await supabase
        .from('ticket_categories')
        .insert(payload)
        .select('*')
        .single();
      if (error) throw error;
      return data as TicketCategory;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ticketKeys.categories() });
      void qc.invalidateQueries({ queryKey: ticketKeys.list() });
    },
  });
}

export function useReorderTicketCategories() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orderedIds: string[]) => {
      await Promise.all(
        orderedIds.map((id, idx) =>
          supabase
            .from('ticket_categories')
            .update({ position: idx + 1 })
            .eq('id', id)
            .then(({ error }) => {
              if (error) throw error;
            }),
        ),
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ticketKeys.categories() });
    },
  });
}

export function useDeleteTicketCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('ticket_categories').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ticketKeys.categories() });
      void qc.invalidateQueries({ queryKey: ticketKeys.list() });
    },
  });
}

// ---------------------------------------------------------------------------
// Tickets — list + detail
// ---------------------------------------------------------------------------

/**
 * Every ticket the caller may see (RLS does the filtering), most
 * recently touched first. The inbox derives its views client-side —
 * volume is small and one query keeps the counts in the chips exact.
 */
export function useTickets(options?: { enabled?: boolean }) {
  const { profile } = useAuth();
  return useQuery({
    queryKey: ticketKeys.list(),
    enabled: !!profile && (options?.enabled ?? true),
    refetchInterval: POLL_MS,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tickets')
        .select(TICKET_SELECT)
        .order('last_activity_at', { ascending: false })
        .limit(1000);
      if (error) throw error;
      return (data ?? []) as unknown as TicketWithContext[];
    },
  });
}

export function useTicket(id: string | null | undefined) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ticketKeys.detail(id ?? ''),
    enabled: !!id,
    placeholderData: () => {
      if (!id) return undefined;
      const list = qc.getQueryData<TicketWithContext[]>(ticketKeys.list());
      return list?.find((t) => t.id === id);
    },
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tickets')
        .select(TICKET_SELECT)
        .eq('id', id!)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as TicketWithContext | null) ?? null;
    },
  });
}

export type TicketView = 'inbox' | 'mine' | 'all' | 'closed';

export interface TicketViewCounts {
  inbox: number;
  mine: number;
  all: number;
  closed: number;
  /** Open tickets nobody has routed yet — the triage tier's first job. */
  unrouted: number;
  /** Open tickets assigned to me that I have not moved past "Seen". */
  waitingOnMe: number;
}

/**
 * Slice the full list into the inbox views.
 *   inbox  — open tickets assigned to me, plus (for triage) open tickets
 *            nobody has routed yet
 *   mine   — requests I made (any status), newest activity first
 *   all    — every open ticket I can see
 *   closed — done / declined / cancelled
 */
export function useTicketViews(tickets: TicketWithContext[] | undefined, canTriage: boolean) {
  const { profile } = useAuth();
  const me = profile?.id;
  return useMemo(() => {
    const rows = tickets ?? [];
    const open = rows.filter((t) => !isTicketClosed(t.status));
    const inbox = open.filter(
      (t) => t.assignee_id === me || (canTriage && t.assignee_id === null),
    );
    const mine = rows.filter((t) => t.requester_id === me);
    const closed = rows.filter((t) => isTicketClosed(t.status));
    const counts: TicketViewCounts = {
      inbox: inbox.length,
      mine: mine.filter((t) => !isTicketClosed(t.status)).length,
      all: open.length,
      closed: closed.length,
      unrouted: open.filter((t) => t.assignee_id === null).length,
      waitingOnMe: open.filter(
        (t) => t.assignee_id === me && (t.status === 'new' || t.status === 'acknowledged'),
      ).length,
    };
    const byView: Record<TicketView, TicketWithContext[]> = {
      inbox,
      mine,
      all: open,
      closed,
    };
    return { byView, counts };
  }, [tickets, me, canTriage]);
}

// ---------------------------------------------------------------------------
// Tickets — mutations
// ---------------------------------------------------------------------------

export interface CreateTicketInput {
  title: string;
  description?: string | null;
  categoryId?: string | null;
  priority?: TicketPriority;
  neededBy?: string | null;
  /** Explicit "send to". Omit to let the category default route it. */
  assigneeId?: string | null;
  /** Triage only: file on someone else's behalf. Defaults to the caller. */
  requesterId?: string | null;
  files?: File[];
}

export function useCreateTicket() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: CreateTicketInput) => {
      if (!profile) throw new Error('Not signed in.');
      const title = input.title.trim();
      if (!title) throw new Error('ticket_title_required');
      const payload: Inserts<'tickets'> = {
        organization_id: profile.organization_id,
        title,
        description: input.description?.trim() || null,
        category_id: input.categoryId ?? null,
        priority: input.priority ?? 'medium',
        needed_by: input.neededBy || null,
        assignee_id: input.assigneeId ?? null,
        requester_id: input.requesterId ?? profile.id,
        created_by: profile.id,
        source: 'app',
      };
      const { data, error } = await supabase
        .from('tickets')
        .insert(payload)
        .select(TICKET_SELECT)
        .single();
      if (error) throw error;
      const ticket = data as unknown as TicketWithContext;

      const failures: string[] = [];
      for (const file of input.files ?? []) {
        try {
          await uploadTicketFile({
            organizationId: profile.organization_id,
            ticketId: ticket.id,
            uploaderId: profile.id,
            file,
          });
        } catch (err) {
          failures.push(`${file.name}: ${err instanceof Error ? err.message : String(err)}`);
        }
      }
      return { ticket, attachmentFailures: failures };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

export interface UpdateTicketInput {
  id: string;
  patch: {
    title?: string;
    description?: string | null;
    category_id?: string | null;
    priority?: TicketPriority;
    status?: TicketStatus;
    assignee_id?: string | null;
    requester_id?: string | null;
    needed_by?: string | null;
    linked_task_id?: string | null;
    resolution?: string | null;
  };
}

export function useUpdateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: UpdateTicketInput) => {
      const { data, error } = await supabase
        .from('tickets')
        .update(patch as Updates<'tickets'>)
        .eq('id', id)
        .select(TICKET_SELECT)
        .single();
      if (error) throw error;
      return data as unknown as TicketWithContext;
    },
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: ticketKeys.detail(id) });
      const prevDetail = qc.getQueryData<TicketWithContext | null>(ticketKeys.detail(id));
      const prevList = qc.getQueryData<TicketWithContext[]>(ticketKeys.list());
      if (prevDetail) {
        qc.setQueryData(ticketKeys.detail(id), { ...prevDetail, ...patch });
      }
      if (prevList) {
        qc.setQueryData(
          ticketKeys.list(),
          prevList.map((t) => (t.id === id ? { ...t, ...patch } : t)),
        );
      }
      return { prevDetail, prevList };
    },
    onError: (_err, { id }, ctx) => {
      if (ctx?.prevDetail !== undefined) qc.setQueryData(ticketKeys.detail(id), ctx.prevDetail);
      if (ctx?.prevList) qc.setQueryData(ticketKeys.list(), ctx.prevList);
    },
    onSettled: (_data, _err, { id }) => {
      void qc.invalidateQueries({ queryKey: ticketKeys.list() });
      void qc.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: ticketKeys.events(id) });
      void qc.invalidateQueries({ queryKey: ticketKeys.watchers(id) });
    },
  });
}

export function useDeleteTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('tickets').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

/**
 * "Add to a task list": create a task in one of my projects from the
 * request and link the two. The task carries the ticket ref in its
 * description so the task list reads back to the ticket.
 */
export function useCreateTaskFromTicket() {
  const createTask = useCreateTask();
  const updateTicket = useUpdateTicket();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      ticket: TicketWithContext;
      projectId: string;
      assigneeIds?: string[];
      dueDate?: string | null;
    }) => {
      const ref = `T-${String(input.ticket.number).padStart(4, '0')}`;
      const task = await createTask.mutateAsync({
        projectId: input.projectId,
        name: `${ref} ${input.ticket.title}`,
        description: [
          `From ticket ${ref}${input.ticket.requester ? ` requested by ${input.ticket.requester.full_name ?? input.ticket.requester.email}` : ''}.`,
          input.ticket.description ?? '',
        ]
          .filter(Boolean)
          .join('\n\n'),
        assigneeIds: input.assigneeIds ?? (input.ticket.assignee_id ? [input.ticket.assignee_id] : []),
        dueDate: input.dueDate ?? input.ticket.needed_by ?? null,
        priority:
          input.ticket.priority === 'urgent'
            ? 'super_high'
            : input.ticket.priority === 'high'
              ? 'high'
              : input.ticket.priority === 'low'
                ? 'low'
                : 'medium',
      });
      await updateTicket.mutateAsync({
        id: input.ticket.id,
        patch: { linked_task_id: task.id },
      });
      return task;
    },
    onSuccess: (_task, vars) => {
      void qc.invalidateQueries({ queryKey: projectKeys.tasks(vars.projectId) });
      void qc.invalidateQueries({ queryKey: [...taskKeys.all, 'mine'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

export function useTicketComments(ticketId: string | null | undefined) {
  return useQuery({
    queryKey: ticketKeys.comments(ticketId ?? ''),
    enabled: !!ticketId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ticket_comments')
        .select(`*, author:author_id(${PERSON})`)
        .eq('ticket_id', ticketId!)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as TicketCommentWithAuthor[];
    },
  });
}

/**
 * Post a comment. Mentions fan out through
 * create_ticket_mention_notifications (which also adds a mentioned
 * non-participant as a follower) and then best-effort invoke
 * notify-deliver so email / Slack fire even where the DB trigger cannot
 * reach pg_net.
 */
export function useCreateTicketComment() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      ticketId: string;
      body: string;
      isInternal?: boolean;
      mentionIds?: string[];
    }) => {
      if (!profile) throw new Error('Not signed in.');
      const body = input.body.trim();
      if (!body) throw new Error('Write a few words first.');
      const payload: Inserts<'ticket_comments'> = {
        organization_id: profile.organization_id,
        ticket_id: input.ticketId,
        author_id: profile.id,
        body,
        is_internal: input.isInternal ?? false,
      };
      const { data, error } = await supabase
        .from('ticket_comments')
        .insert(payload)
        .select(`*, author:author_id(${PERSON})`)
        .single();
      if (error) throw error;
      const comment = data as unknown as TicketCommentWithAuthor;

      const mentions = Array.from(new Set(input.mentionIds ?? [])).filter(
        (id) => id && id !== profile.id,
      );
      if (mentions.length > 0 && !input.isInternal) {
        const { data: ids, error: mentionErr } = await supabase.rpc(
          'create_ticket_mention_notifications',
          {
            p_recipient_ids: mentions,
            p_ticket_id: input.ticketId,
            p_comment_id: comment.id,
            p_snippet: body.slice(0, 280),
          },
        );
        if (mentionErr) {
          console.warn('ticket mention fan-out failed', mentionErr);
        } else if (Array.isArray(ids) && ids.length > 0) {
          void supabase.functions
            .invoke('notify-deliver', { body: { notification_ids: ids } })
            .then(({ error: deliverErr }) => {
              if (deliverErr) console.warn('notify-deliver failed', deliverErr);
            });
        }
      }
      return comment;
    },
    onSuccess: (_c, vars) => {
      void qc.invalidateQueries({ queryKey: ticketKeys.comments(vars.ticketId) });
      void qc.invalidateQueries({ queryKey: ticketKeys.watchers(vars.ticketId) });
      void qc.invalidateQueries({ queryKey: ticketKeys.list() });
      void qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useDeleteTicketComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; ticketId: string }) => {
      const { error } = await supabase.from('ticket_comments').delete().eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ticketKeys.comments(vars.ticketId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Events (append-only, trigger-written)
// ---------------------------------------------------------------------------

export function useTicketEvents(ticketId: string | null | undefined) {
  return useQuery({
    queryKey: ticketKeys.events(ticketId ?? ''),
    enabled: !!ticketId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ticket_events')
        .select(`*, actor:actor_id(${PERSON})`)
        .eq('ticket_id', ticketId!)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as TicketEventWithActor[];
    },
  });
}

// ---------------------------------------------------------------------------
// Attachments
// ---------------------------------------------------------------------------

const TICKET_ATTACHMENTS_BUCKET: TicketAttachmentBucket = 'ticket-attachments';

function safeFilename(name: string): string {
  const cleaned = name.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
  return cleaned || 'file';
}

async function uploadTicketFile(input: {
  organizationId: string;
  ticketId: string;
  uploaderId: string;
  file: File;
}): Promise<TicketAttachment> {
  const path = `${input.organizationId}/${input.ticketId}/${Date.now()}-${safeFilename(input.file.name)}`;
  const { error: uploadErr } = await supabase.storage
    .from(TICKET_ATTACHMENTS_BUCKET)
    .upload(path, input.file, {
      upsert: false,
      cacheControl: '3600',
      contentType: input.file.type || undefined,
    });
  if (uploadErr) throw uploadErr;

  const payload: Inserts<'ticket_attachments'> = {
    organization_id: input.organizationId,
    ticket_id: input.ticketId,
    bucket: TICKET_ATTACHMENTS_BUCKET,
    storage_path: path,
    file_name: input.file.name,
    mime_type: input.file.type || null,
    size_bytes: input.file.size,
    uploaded_by: input.uploaderId,
  };
  const { data, error } = await supabase
    .from('ticket_attachments')
    .insert(payload)
    .select('*')
    .single();
  if (error) {
    await supabase.storage.from(TICKET_ATTACHMENTS_BUCKET).remove([path]).catch(() => undefined);
    throw error;
  }
  return data as TicketAttachment;
}

export function useTicketAttachments(ticketId: string | null | undefined) {
  return useQuery({
    queryKey: ticketKeys.attachments(ticketId ?? ''),
    enabled: !!ticketId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ticket_attachments')
        .select(`*, uploader:uploaded_by(${PERSON})`)
        .eq('ticket_id', ticketId!)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as TicketAttachmentWithUploader[];
    },
  });
}

export function useUploadTicketAttachment() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { ticketId: string; file: File }) => {
      if (!profile) throw new Error('Not signed in.');
      return uploadTicketFile({
        organizationId: profile.organization_id,
        ticketId: input.ticketId,
        uploaderId: profile.id,
        file: input.file,
      });
    },
    onSuccess: (_a, vars) => {
      void qc.invalidateQueries({ queryKey: ticketKeys.attachments(vars.ticketId) });
      void qc.invalidateQueries({ queryKey: ticketKeys.events(vars.ticketId) });
      void qc.invalidateQueries({ queryKey: ticketKeys.list() });
    },
  });
}

export function useDeleteTicketAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: TicketAttachment) => {
      const { error } = await supabase.from('ticket_attachments').delete().eq('id', a.id);
      if (error) throw error;
      if (a.bucket === TICKET_ATTACHMENTS_BUCKET) {
        await supabase.storage.from(a.bucket).remove([a.storage_path]).catch(() => undefined);
      }
    },
    onSuccess: (_d, a) => {
      void qc.invalidateQueries({ queryKey: ticketKeys.attachments(a.ticket_id) });
    },
  });
}

/** Short-lived signed URL for a private attachment (either bucket). */
export function useTicketSignedUrl(
  bucket: TicketAttachmentBucket | null | undefined,
  path: string | null | undefined,
) {
  return useQuery({
    queryKey: ticketKeys.signedUrl(bucket ?? '', path ?? ''),
    enabled: !!bucket && !!path,
    staleTime: 4 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.storage.from(bucket!).createSignedUrl(path!, 60 * 5);
      if (error) throw error;
      return data?.signedUrl ?? null;
    },
  });
}

// ---------------------------------------------------------------------------
// Watchers
// ---------------------------------------------------------------------------

export function useTicketWatchers(ticketId: string | null | undefined) {
  return useQuery({
    queryKey: ticketKeys.watchers(ticketId ?? ''),
    enabled: !!ticketId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ticket_watchers')
        .select(`*, profile:profile_id(${PERSON})`)
        .eq('ticket_id', ticketId!)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as TicketWatcherWithProfile[];
    },
  });
}

export function useSetTicketWatchers() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { ticketId: string; add: string[]; remove: string[] }) => {
      if (!profile) throw new Error('Not signed in.');
      if (input.remove.length > 0) {
        const { error } = await supabase
          .from('ticket_watchers')
          .delete()
          .eq('ticket_id', input.ticketId)
          .in('profile_id', input.remove);
        if (error) throw error;
      }
      if (input.add.length > 0) {
        const rows: Inserts<'ticket_watchers'>[] = Array.from(new Set(input.add)).map(
          (profileId) => ({
            ticket_id: input.ticketId,
            profile_id: profileId,
            organization_id: profile.organization_id,
            added_by: profile.id,
          }),
        );
        const { error } = await supabase
          .from('ticket_watchers')
          .upsert(rows, { onConflict: 'ticket_id,profile_id', ignoreDuplicates: true });
        if (error) throw error;
      }
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ticketKeys.watchers(vars.ticketId) });
      void qc.invalidateQueries({ queryKey: ticketKeys.list() });
    },
  });
}

// ---------------------------------------------------------------------------
// Realtime
// ---------------------------------------------------------------------------

// One shared channel per browser tab, reference-counted so the sidebar
// badge and the inbox page can both mount the hook without opening
// duplicate subscriptions.
let realtimeChannel: ReturnType<typeof supabase.channel> | null = null;
let realtimeRefs = 0;
let realtimeOrg: string | null = null;

/**
 * Keep the inbox live. RLS applies to change events, so each person only
 * receives rows they may see; we invalidate rather than patch because
 * the list query carries embeds the payload does not.
 */
export function useTicketsRealtime(enabled = true) {
  const qc = useQueryClient();
  const { profile } = useAuth();
  const orgId = profile?.organization_id;
  useEffect(() => {
    if (!enabled || !orgId) return;
    realtimeRefs += 1;
    if (realtimeChannel && realtimeOrg !== orgId) {
      void supabase.removeChannel(realtimeChannel);
      realtimeChannel = null;
    }
    if (!realtimeChannel) {
      realtimeOrg = orgId;
      realtimeChannel = buildTicketsChannel(qc, orgId).subscribe();
    }
    return () => {
      realtimeRefs -= 1;
      if (realtimeRefs <= 0 && realtimeChannel) {
        void supabase.removeChannel(realtimeChannel);
        realtimeChannel = null;
        realtimeOrg = null;
        realtimeRefs = 0;
      }
    };
  }, [enabled, orgId, qc]);
}

function buildTicketsChannel(qc: ReturnType<typeof useQueryClient>, orgId: string) {
  return supabase
    .channel(`tickets:${orgId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'tickets', filter: `organization_id=eq.${orgId}` },
      (payload) => {
        const row = (payload.new ?? payload.old) as Partial<Ticket>;
        void qc.invalidateQueries({ queryKey: ticketKeys.list() });
        if (row?.id) {
          void qc.invalidateQueries({ queryKey: ticketKeys.detail(row.id) });
          void qc.invalidateQueries({ queryKey: ticketKeys.events(row.id) });
        }
        void qc.invalidateQueries({ queryKey: notificationKeys.all });
      },
    )
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'ticket_comments',
        filter: `organization_id=eq.${orgId}`,
      },
      (payload) => {
        const row = payload.new as Partial<TicketComment>;
        if (row?.ticket_id) {
          void qc.invalidateQueries({ queryKey: ticketKeys.comments(row.ticket_id) });
        }
        void qc.invalidateQueries({ queryKey: ticketKeys.list() });
        void qc.invalidateQueries({ queryKey: notificationKeys.all });
      },
    )
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'ticket_events',
        filter: `organization_id=eq.${orgId}`,
      },
      (payload) => {
        const row = payload.new as Partial<TicketEvent>;
        if (row?.ticket_id) {
          void qc.invalidateQueries({ queryKey: ticketKeys.events(row.ticket_id) });
          void qc.invalidateQueries({ queryKey: ticketKeys.attachments(row.ticket_id) });
        }
      },
    );
}

// ---------------------------------------------------------------------------
// Work Mode (kiosk) — the worker is the session, not auth.uid()
// ---------------------------------------------------------------------------

export interface WorkTicketSummary {
  id: string;
  number: number;
  ref: string;
  title: string;
  description: string | null;
  status: TicketStatus;
  status_label: string;
  priority: TicketPriority;
  category_id: string | null;
  category_name: string | null;
  category_slug: string | null;
  assignee_id: string | null;
  assignee_name: string | null;
  needed_by: string | null;
  resolution: string | null;
  created_at: string;
  last_activity_at: string;
  resolved_at: string | null;
  comment_count: number;
  last_comment: { body: string; author_name: string; created_at: string } | null;
}

export interface WorkTicketThread {
  ticket: Ticket & {
    ref: string;
    status_label: string;
    assignee_name: string | null;
    category_name: string | null;
  };
  comments: Array<{
    id: string;
    body: string;
    author_id: string | null;
    author_name: string;
    created_at: string;
    mine: boolean;
  }>;
  events: Array<{
    id: string;
    kind: TicketEvent['kind'];
    from_value: string | null;
    to_value: string | null;
    meta: TicketEvent['meta'];
    actor_name: string | null;
    created_at: string;
  }>;
}

const WORK_POLL_MS = 30_000;

export function useWorkTickets(sessionId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ticketKeys.work.list(sessionId ?? ''),
    enabled: !!sessionId && enabled,
    refetchInterval: WORK_POLL_MS,
    queryFn: () => workRpc<WorkTicketSummary[]>('work_ticket_list', { p_session_id: sessionId }),
  });
}

export function useWorkTicketThread(
  sessionId: string | null | undefined,
  ticketId: string | null | undefined,
) {
  return useQuery({
    queryKey: ticketKeys.work.thread(sessionId ?? '', ticketId ?? ''),
    enabled: !!sessionId && !!ticketId,
    refetchInterval: WORK_POLL_MS,
    queryFn: () =>
      workRpc<WorkTicketThread>('work_ticket_thread', {
        p_session_id: sessionId,
        p_ticket_id: ticketId,
      }),
  });
}

export function useSubmitWorkTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      sessionId: string;
      title: string;
      categoryId?: string | null;
      description?: string | null;
      priority?: TicketPriority;
      photoPaths?: string[];
    }) =>
      workRpc<Ticket>('work_ticket_submit', {
        p_session_id: input.sessionId,
        p_title: input.title,
        p_category_id: input.categoryId ?? null,
        p_description: input.description?.trim() || null,
        p_priority: input.priority ?? 'medium',
        p_photo_paths: input.photoPaths ?? [],
      }),
    onSuccess: (_t, vars) => {
      void qc.invalidateQueries({ queryKey: ticketKeys.work.list(vars.sessionId) });
      void qc.invalidateQueries({ queryKey: ticketKeys.list() });
    },
  });
}

export function useWorkTicketComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sessionId: string; ticketId: string; body: string }) =>
      workRpc<TicketComment>('work_ticket_comment', {
        p_session_id: input.sessionId,
        p_ticket_id: input.ticketId,
        p_body: input.body,
      }),
    onSuccess: (_c, vars) => {
      void qc.invalidateQueries({ queryKey: ticketKeys.work.thread(vars.sessionId, vars.ticketId) });
      void qc.invalidateQueries({ queryKey: ticketKeys.work.list(vars.sessionId) });
    },
  });
}

/**
 * Categories for the tablet. The kiosk account is an org member, so the
 * plain read policy applies — no RPC needed.
 */
export function useWorkTicketCategories(enabled = true) {
  return useQuery({
    queryKey: [...ticketKeys.categories(), 'work'],
    enabled,
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ticket_categories')
        .select('id, slug, name, description, position')
        .eq('is_active', true)
        .order('position', { ascending: true });
      if (error) throw error;
      return (data ?? []) as Array<
        Pick<TicketCategory, 'id' | 'slug' | 'name' | 'description' | 'position'>
      >;
    },
  });
}
